import React, { createContext, useContext, useState, useEffect } from 'react';

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail) {
        // Check if user is SuperAdmin - SuperAdmins don't have tenant data
        if (event.detail.is_super_admin) {
          console.log('TenantContext: SuperAdmin detected, skipping tenant fetch');
          setTenant(null);
          setEntitlements(null);
          setUsage(null);
          setLoading(false);
          return;
        }
        // User logged in or auth checked
        console.log('TenantContext: Regular user, fetching tenant info');
        fetchTenantInfo();
      } else {
        // User logged out
        console.log('TenantContext: User logged out, clearing tenant data');
        setTenant(null);
        setEntitlements(null);
        setUsage(null);
        setLoading(false);
      }
    };

    window.addEventListener('auth-state-changed', handleAuthChange);
    
    // Initial fetch - check if user exists and is not SuperAdmin
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('TenantContext: Initial check, user found:', userData.email, 'SuperAdmin:', userData.is_super_admin);
        if (!userData.is_super_admin) {
          fetchTenantInfo();
        } else {
          console.log('TenantContext: SuperAdmin detected on init, skipping tenant fetch');
          setLoading(false);
        }
      } catch (err) {
        console.error('TenantContext: Error parsing stored user:', err);
        setLoading(false);
      }
    } else {
      console.log('TenantContext: No stored user, attempting fetch (will fail gracefully for SuperAdmin)');
      fetchTenantInfo();
    }

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
    };
  }, []);

  // Fetch tenant information
  const fetchTenantInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/tenants/current`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Not authenticated, clear tenant info
          setTenant(null);
          setEntitlements(null);
          setUsage(null);
          setLoading(false);
          return;
        }
        if (response.status === 404) {
          // Tenant not found - likely a SuperAdmin user
          console.log('No tenant found (SuperAdmin user)');
          setTenant(null);
          setEntitlements(null);
          setUsage(null);
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch tenant information');
      }

      const tenantData = await response.json();
      setTenant(tenantData);

      // Fetch entitlements
      const entitlementsResponse = await fetch(`${BACKEND_URL}/api/tenants/current/entitlements`, {
        credentials: 'include',
      });

      if (entitlementsResponse.ok) {
        const entitlementsData = await entitlementsResponse.json();
        setEntitlements(entitlementsData);
      }

      // Fetch usage
      const usageResponse = await fetch(`${BACKEND_URL}/api/tenants/current/usage`, {
        credentials: 'include',
      });

      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsage(usageData);
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh tenant data
  const refreshTenant = async () => {
    await fetchTenantInfo();
  };

  // Check if a feature is enabled for the tenant
  const hasFeature = (feature) => {
    if (!entitlements) return false;

    switch (feature) {
      case 'ai_intro_mail':
        return entitlements.ai_intro_mail_enabled === true;
      default:
        return false;
    }
  };

  // Check if tenant is within usage limits
  const isWithinLimit = (resource) => {
    if (!usage) return true;

    switch (resource) {
      case 'users':
        return usage.current_users < usage.max_users;
      case 'leads':
        return usage.current_leads < usage.max_leads;
      default:
        return true;
    }
  };

  // Get usage percentage for a resource
  const getUsagePercentage = (resource) => {
    if (!usage) return 0;

    switch (resource) {
      case 'users':
        return usage.max_users > 0 
          ? Math.round((usage.current_users / usage.max_users) * 100)
          : 0;
      case 'leads':
        return usage.max_leads > 0
          ? Math.round((usage.current_leads / usage.max_leads) * 100)
          : 0;
      default:
        return 0;
    }
  };

  // Get plan display name
  const getPlanName = () => {
    if (!entitlements) return 'Unknown';
    return entitlements.plan_name || 'Unknown';
  };

  // Get plan tier (base, tier1, tier2)
  const getPlanTier = () => {
    if (!entitlements) return null;
    return entitlements.plan_key;
  };

  // Check if plan is Base
  const isBasePlan = () => {
    return getPlanTier() === 'base';
  };

  // Check if plan is Tier1 or higher
  const isTier1OrHigher = () => {
    const tier = getPlanTier();
    return tier === 'tier1' || tier === 'tier2';
  };

  // Check if plan is Tier2
  const isTier2 = () => {
    return getPlanTier() === 'tier2';
  };

  const value = {
    tenant,
    entitlements,
    usage,
    loading,
    error,
    refreshTenant,
    hasFeature,
    isWithinLimit,
    getUsagePercentage,
    getPlanName,
    getPlanTier,
    isBasePlan,
    isTier1OrHigher,
    isTier2,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
